import {
  Box,
  Button,
  Checkbox,
  Flex,
  Form,
  Heading,
  Icon,
  InlineStack,
  PlainButton,
  Spinner,
  Stack,
  Text,
  TextField,
  Tooltip,
} from '@customerio/pluma-components/react';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '@/services/auth';
import { CreditCardDetails } from './credit-card-details';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import { isEmptyString } from '@/utils/string';
import defaultAccountLogo from '@/assets/images/default-account-logo.svg';
import { BillingAddressFields } from './billing-address-fields';
import { CardFields } from './card-fields';
import { useBillingStripePromise } from '../hooks/use-billing-stripe';
import { FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE } from '@/features/billing-usage';
import type { CreditsPurchaseConfig } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import { CreditsPurchaseDisclaimer } from './credits-purchase-disclaimer';
import { CreditsPurchasePackPicker, packNounFor } from './credits-purchase-pack-picker';
import type { CreditsPurchaseCardView, CreditsPurchaseTotals } from '../utils/credits-purchase-form-helpers';
import {
  estimatedSends as computeEstimatedSends,
  creditsPurchaseCardView,
  disclaimerRateNote,
  showsBillingAddress,
  STRIPE_LOAD_ERROR_MESSAGE,
} from '../utils/credits-purchase-form-helpers';
import * as styles from './credits-purchase.css';
import { useCreditsPurchaseForm } from '../hooks/use-credits-purchase-form';

export type CreditsPurchaseFormProps = {
  config: CreditsPurchaseConfig;
  /** The surface that opened the form, reported with every event it fires. */
  pageName: CreditsPurchasePageName;
  /** Current balance in cents; caps the purchasable quantity for balance-limited configs (Builder). */
  currentBalanceCents?: number;
  /** The quantity to open on. Defaults to the config's own default. Not held to the balance cap. */
  initialQuantity?: number;
  /** Per-message Builder cost for the estimated-sends hint; defaults to the canonical fallback. */
  costPerMessage?: number;
  onSuccess: (info: { savedCard: boolean }) => void;
  onCancel: () => void;
  onProcessingChange?: (isProcessing: boolean) => void;
};

/**
 * The shared credit-purchase form for Stripe-billed accounts (AI credits and Builder credits). Wraps
 * the fields in a Stripe `<Elements>` provider (null in test mode) and delegates state, tax, and the
 * charge orchestration to {@link useCreditsPurchaseForm}.
 */
export function CreditsPurchaseForm(props: CreditsPurchaseFormProps) {
  const stripe = useBillingStripePromise();

  return (
    <Elements stripe={stripe}>
      <CreditsPurchaseFormFields {...props} />
    </Elements>
  );
}

function CreditsPurchaseFormFields({
  config,
  pageName,
  currentBalanceCents,
  initialQuantity,
  costPerMessage,
  onSuccess,
  onCancel,
  onProcessingChange,
}: CreditsPurchaseFormProps) {
  const form = useCreditsPurchaseForm(config, currentBalanceCents, {
    pageName,
    initialQuantity,
    onSuccess,
    onProcessingChange,
  });

  const cardView = creditsPurchaseCardView({
    isStripeLoadError: form.stripeLoad.isError,
    isStripeLoading: form.stripeLoad.isLoading,
    useSavedCard: form.useSavedCard,
  });

  return (
    <Form autoLoading={false} onSubmit={() => form.onSubmit()}>
      <Stack gap="150">
        <Flex alignItems="baseline" justifyContent="space-between">
          <Heading level="2" size="sm">
            Payment method
          </Heading>
          {form.hasSavedCard ? (
            <PlainButton onClick={form.onTogglePaymentMethod}>
              <InlineStack gap="050" alignBlock="center">
                <Text color="accent" size="xs" weight="bold">
                  {form.useSavedCard ? 'Use new card' : 'Use saved card'}
                </Text>
                <Icon name="chevron-right" size="sm" color="accent" />
              </InlineStack>
            </PlainButton>
          ) : null}
        </Flex>

        <CreditsPurchaseCardSection view={cardView} config={config} form={form} />
      </Stack>
      {showsBillingAddress(cardView) ? (
        <Stack mt="350" gap="150">
          <Heading level="2" size="sm">
            Billing address
          </Heading>
          <BillingAddressFields
            address={form.address}
            canEdit={form.canEdit}
            errors={form.visibleErrors}
            onChange={form.onAddressChange}
            onBlur={form.onFieldBlur}
          />
        </Stack>
      ) : null}
      <Stack mt="200" gap="150">
        <Heading level="2" size="sm">
          {config.quantitySectionHeading}
        </Heading>

        {config.packs == null ? (
          <TextField
            data-test-credits-quantity-input
            className={styles.numberInput}
            label={config.quantityLabel}
            type="number"
            value={String(form.quantity)}
            isDisabled={form.isSubmitting}
            isInvalid={form.quantityError != null}
            error={form.quantityError}
            min={form.minQuantity}
            max={form.maxQuantity}
            step={1}
            onInput={form.onQuantityInput}
            onBlur={form.onQuantityBlur}
          />
        ) : (
          <CreditsPurchasePackPicker
            packs={config.packs}
            packNoun={packNounFor(config)}
            unitLabel={config.creditsUnitLabel ?? 'credits'}
            value={form.quantity}
            isDisabled={form.isSubmitting}
            onChange={form.onPackSelect}
          />
        )}

        <CreditsPurchaseTotal config={config} totals={form.totals} costPerMessage={costPerMessage} />
      </Stack>
      <CreditsPurchaseDisclaimer
        disclaimer={config.disclaimer}
        rateNote={disclaimerRateNote(config, costPerMessage ?? FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE)}
        termsLinks={config.termsLinks}
      />
      <Flex mt="300" justifyContent="flex-end" gap="200">
        <Button data-test-credits-purchase-cancel variant="secondary" isDisabled={form.isSubmitting} onClick={onCancel}>
          Cancel
        </Button>

        <Tooltip content={form.submitTooltip} isDisabled={form.submitTooltip === ''} isCustom={true} placement="top">
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              data-test-credits-purchase-submit
              variant="primary"
              type="submit"
              isDisabled={form.submitDisabled}
              isLoading={form.isSubmitting}
            >
              {config.submitLabel ?? 'Buy credits'}
            </Button>
          )}
        </Tooltip>
      </Flex>
    </Form>
  );
}

type CreditsPurchaseCardSectionProps = {
  view: CreditsPurchaseCardView;
  config: CreditsPurchaseConfig;
  form: ReturnType<typeof useCreditsPurchaseForm>;
};

/**
 * The payment-method control for the current {@link CreditsPurchaseCardView}: the ad-blocker message
 * when Stripe.js failed to load, the card already on file, nothing at all while Stripe.js is still in
 * flight, or the new-card fields.
 */
function CreditsPurchaseCardSection({ view, config, form }: CreditsPurchaseCardSectionProps) {
  switch (view) {
    case 'stripe-error':
      return (
        <Text color="critical" data-test-credits-stripe-load-error>
          {STRIPE_LOAD_ERROR_MESSAGE}
        </Text>
      );
    case 'saved-card':
      return <SavedCardSection savedCard={form.savedCard} />;
    case 'stripe-loading':
      return null;
    case 'new-card':
      return <NewCardSection config={config} nameError={form.visibleErrors.name} form={form} />;
    default: {
      const _exhaustive: never = view;
      return _exhaustive;
    }
  }
}

type SavedCardSectionProps = {
  savedCard: ReturnType<typeof useCreditsPurchaseForm>['savedCard'];
};

function SavedCardSection({ savedCard }: SavedCardSectionProps) {
  const { account } = useAuth();

  if (savedCard.isCardLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" style={{ minHeight: '154px' }}>
        <InlineStack alignBlock="center" gap="050">
          <Spinner />
          <Text size="sm">Loading payment method…</Text>
        </InlineStack>
      </Flex>
    );
  }
  if (savedCard.isCardError) {
    return <Text color="critical">Unable to load your card details. Refresh the page or try using a new card.</Text>;
  }
  return (
    <CreditCardDetails
      card={savedCard.cardDetails}
      hasCard={true}
      accountName={account.name}
      accountLogoUrl={isEmptyString(account.logo_url) ? defaultAccountLogo : account.logo_url}
    />
  );
}

type NewCardSectionProps = {
  config: CreditsPurchaseConfig;
  nameError: string | undefined;
  form: ReturnType<typeof useCreditsPurchaseForm>;
};

function NewCardSection({ config, nameError, form }: NewCardSectionProps) {
  return (
    <>
      <CardFields onChange={form.setIsCardComplete} />
      {/* `fs-mask`: the cardholder name is PII — keep it out of FullStory. */}
      <TextField
        data-test-credits-name-input
        className="fs-mask"
        label="Full name"
        value={form.name}
        error={nameError}
        onInput={(event) => form.onNameInput(event.target instanceof HTMLInputElement ? event.target.value : '')}
        onBlur={() => form.onFieldBlur('name')}
      />
      {config.allowSaveCard ? (
        <Checkbox
          data-test-credits-save-card
          isChecked={form.saveCard}
          label="Save card for future purchases"
          onCheckedChange={form.onSaveCardChange}
        />
      ) : null}
    </>
  );
}

type CreditsPurchaseTotalProps = {
  config: CreditsPurchaseConfig;
  totals: CreditsPurchaseTotals;
  /** Per-message Builder cost for the estimated-sends hint; defaults to the canonical fallback. */
  costPerMessage: number | undefined;
};

/** The tax-inclusive total, plus the Builder sends estimate and the credit breakdown when they apply. */
function CreditsPurchaseTotal({ config, totals, costPerMessage }: CreditsPurchaseTotalProps) {
  const estimatedSends = computeEstimatedSends(
    config,
    totals.totalPriceCents,
    costPerMessage ?? FALLBACK_BUILDER_CREDIT_COST_PER_MESSAGE,
  );

  return (
    <Box data-test-credits-price-total>
      <Text weight="bold">
        Total: {formatCentsToUsd(totals.totalWithTaxCents)} including tax
        {estimatedSends != null ? ` (about ${formatNumber(estimatedSends)} sends)` : ''}.
      </Text>
      {config.showCreditsBreakdown ? (
        <Text color="secondary">
          {' '}
          ({formatNumber(totals.totalCredits)} {config.creditsUnitLabel})
        </Text>
      ) : null}
    </Box>
  );
}
